<template>
    <div>
    <base-dialog :loading="loading" :value="dialog" :width="480"
        :title="`${editMode ? $t('common.edit') : $t('common.add')} ${$t('common.note')}`"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="note-body">
            <kimsa-text-field bolder-label dense readonly :label="$t('common.date')"
                :value="date" force-value />
            <div v-if="studentId" class="title-pin-row">
                <kimsa-text-field class="flex-grow-1" bolder-label dense :label="$t('common.title')"
                    :placeholder="$t('common.write_here')" :value="title" @change="title = $event" force-value />
                <div class="pin-block">
                    <div class="pin-label">{{ $t('common.pin') }}</div>
                    <button type="button" class="pin-btn" :class="{ 'pin-btn--on': pinned }" @click="pinned = !pinned">
                        <v-icon size="18" :color="pinned ? '#FFFFFF' : 'txtSecondary'">mdi-pin</v-icon>
                    </button>
                </div>
            </div>
            <kimsa-textarea-field bolder-label :label="$t('common.message')" :rows="5"
                :placeholder="$t('common.write_here')" @change="message = $event" :value="message" force-value />
        </div>

        <template v-slot:footer>
            <v-btn v-if="editMode" block depressed x-large color="error" class="footer-btn"
                :loading="loadingDelete" @click="deleteNotes">
                {{ $t('common.delete') }}
            </v-btn>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text"
                :disabled="disabled" @click="setNotes" :loading="loading">
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </base-dialog>

    <kimsa-confirm ref="confirmDelete" :title="$t('common.delete_note')" :content="$t('common.confirm_delete_note')"
        :loading="loadingDelete" :persistent="false" :close-on-confirm="false" :alert-mode="true"
        @confirm="onConfirmDelete" />
    </div>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { db, Timestamp } from "@/utils/Firebase";
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";
import BaseDialog from "@/components/common/BaseDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";

export default {
    name: "NotesStudentDialog",
    components: { KimsaTextareaField, KimsaTextField, BaseDialog, KimsaConfirm },
    props: {
        studentId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingDelete: false,
            student: false,
            date: null,
            message: '',
            editMode: false,
            title: '',
            pinned: false,
            noteId: null,
        }
    },
    computed: {
        disabled(vm = this) {
            if (vm.studentId) return !vm.message || !vm.title
            return !vm.message
        }
    },
    methods: {
        show(student = null) {
            const vm = this
            if (student) vm.setDefaults(student)
            else vm.date = vm.$moment().format('DD-MM-YYYY')
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.student = null
            vm.message = ''
            vm.date = null
            vm.editMode = false
            vm.title = ''
            vm.pinned = false
            vm.noteId = null
        },
        setDefaults(student) {
            const vm = this
            vm.student = student
            vm.message = student?.coachNote?.message || ''
            vm.date = student?.coachNote?.date || vm.$moment().format('DD-MM-YYYY')
            vm.editMode = !!vm.message
        },
        setDefaultsProfileSection(note) {
            const vm = this
            vm.show()
            vm.noteId = note.id
            vm.message = note.message || ''
            vm.date = note.date || vm.$moment().format('DD-MM-YYYY')
            vm.title = note.title || ''
            vm.pinned = note.pinned || false
            vm.editMode = true
        },
        async setNotes() {
            const vm = this
            if (vm.loadingDelete) return;
            await vm.withLoading(async () => {
                if (vm.studentId) {
                    if (vm.editMode) {
                        await db.collection('studentNotes').doc(vm.noteId).update({
                            title: vm.title,
                            message: vm.message,
                            pinned: vm.pinned,
                        })
                    } else {
                        await db.collection('studentNotes').add({
                            studentId: vm.studentId,
                            coachId: vm.currentUser.uid,
                            title: vm.title,
                            message: vm.message,
                            date: vm.date,
                            pinned: vm.pinned,
                            createdAt: Timestamp.now()
                        })
                    }
                    vm.$emit('saved')
                } else {
                    await db.collection('users').doc(vm.student.id).update({
                        'coachNote.message': vm.message,
                        'coachNote.date': vm.date,
                    })
                    vm.$emit('saved', vm.student.id, { message: vm.message, date: vm.date })
                }

                vm.toast(vm.$t('common.note_saved'))
                vm.hide()
            })
        },
        deleteNotes() {
            const vm = this
            if (vm.loading) return;
            vm.$refs.confirmDelete.show()
        },
        async onConfirmDelete() {
            const vm = this
            await vm.withLoading(async () => {
                if (vm.studentId) {
                    await db.collection('studentNotes').doc(vm.noteId).delete()
                    vm.$emit('saved')
                } else {
                    await db.collection('users').doc(vm.student.id).update({
                        'coachNote': null,
                    })
                    vm.$emit('saved', vm.student.id, null)
                }

                vm.$refs.confirmDelete?.hide()
                vm.toast(vm.$t('common.note_deleted'))
                vm.hide()
            }, 'loadingDelete')
        }
    },
}
</script>

<style scoped>
.note-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.title-pin-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
}

.pin-block {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
}

.pin-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.pin-btn {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}

.pin-btn:hover {
    border-color: var(--v-secondary-base);
}

.pin-btn--on {
    background: var(--v-secondary-base);
    border-color: var(--v-secondary-base);
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}
</style>